// 聊天消息管理 Hook

import { useState, useCallback, useRef, useEffect } from 'react';
import { Message, ServerMessage } from '../types';
import { MessageUtils } from '../utils/message';
import { ChatApiService } from '../services/api';

// 消息管理 Hook
export function useChatMessages(apiService: ChatApiService) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [lastMessageTimestamp, setLastMessageTimestamp] = useState(0);

  // 添加消息
  const addMessage = useCallback((content: string, type: 'user' | 'bot', status: 'sending' | 'sent' | 'failed' = 'sent') => {
    const newMessage = MessageUtils.createMessage(content, type, status);
    setMessages(prev => {
      const allMessages = [...prev, newMessage];
      const sorted = MessageUtils.sortMessagesByTimestamp(allMessages);
      const limited = MessageUtils.limitMessageCount(sorted, 1000); // 限制最多1000条消息
      return MessageUtils.limitNewAnimations(limited);
    });
  }, []);

  // 批量添加消息 - 新增优化方法
  const addMessages = useCallback((messages: Array<{content: string, type: 'user' | 'bot', status?: 'sending' | 'sent' | 'failed'}>) => {
    if (messages.length === 0) return;
    
    const newMessages = messages.map(msg => MessageUtils.createMessage(msg.content, msg.type, msg.status || 'sent'));
    
    setMessages(prev => {
      const allMessages = [...prev, ...newMessages];
      const sorted = MessageUtils.sortMessagesByTimestamp(allMessages);
      const limited = MessageUtils.limitMessageCount(sorted, 1000);
      return MessageUtils.limitNewAnimations(limited);
    });
  }, []);

  // 更新消息状态
  const updateMessageStatus = useCallback((messageId: string, status: 'sending' | 'sent' | 'failed', retryCount?: number) => {
    setMessages(prev => {
      const updated = MessageUtils.updateMessageStatus(prev, messageId, status, retryCount);
      const sorted = MessageUtils.sortMessagesByTimestamp(updated);
      const limited = MessageUtils.limitMessageCount(sorted, 1000);
      return MessageUtils.limitNewAnimations(limited);
    });
  }, []);

  // 标记消息为已读
  const markMessageAsRead = useCallback((messageKey: string) => {
    setMessages(prev => MessageUtils.markMessageAsRead(prev, messageKey));
  }, []);

  // 获取消息
  const fetchMessages = useCallback(async (
    email: string, 
    tempUserKey: string, 
    page: number = 1, 
    pagesize: number = 10, 
    isPolling: boolean = false
  ) => {
    try {
      const result = await apiService.getMessages({
        email,
        temp_user_key: tempUserKey,
        page,
        pagesize
      });

      const serverMessages = result.data.data;

      if (isPolling) {
        // 轮询模式：只添加新消息
        setMessages(prev => {
          const newServerMessages = MessageUtils.filterNewMessages(serverMessages, prev);
          if (newServerMessages.length > 0) {
            const newMessages = newServerMessages.map(serverMsg => 
              MessageUtils.convertServerMessage(serverMsg, true)
            );
            const maxTimestamp = Math.max(...newMessages.map(msg => msg.timestamp.getTime() / 1000));
            setLastMessageTimestamp(maxTimestamp);
            const merged = [...prev, ...newMessages];
            const sorted = MessageUtils.sortMessagesByTimestamp(merged);
            return MessageUtils.limitNewAnimations(sorted);
          }
          return prev;
        });
      } else {
        // 分页模式：处理历史消息
        const convertedMessages = serverMessages.map(serverMsg => 
          MessageUtils.convertServerMessage(serverMsg, false)
        );

        if (page === 1) {
          // 第一页：替换所有消息并排序
          const sortedMessages = MessageUtils.sortMessagesByTimestamp(convertedMessages);
          const limited = MessageUtils.limitMessageCount(sortedMessages, 1000);
          setMessages(MessageUtils.limitNewAnimations(limited));
          if (limited.length > 0) {
            setLastMessageTimestamp(MessageUtils.getLastMessageTimestamp(limited));
          }
        } else {
          // 后续页：合并历史消息并排序
          setMessages(prev => {
            const allMessages = [...convertedMessages, ...prev];
            const sorted = MessageUtils.sortMessagesByTimestamp(allMessages);
            const limited = MessageUtils.limitMessageCount(sorted, 1000);
            return MessageUtils.limitNewAnimations(limited);
          });
        }

        setCurrentPage(result.data.page);
        setTotalPages(result.data.max_page);
        const hasMore = result.data.page < result.data.max_page;
        setHasMoreHistory(hasMore);
        
        // 添加调试信息
        console.log('分页信息:', {
          currentPage: result.data.page,
          maxPage: result.data.max_page,
          totalCount: result.data.all_count,
          hasMore,
          messagesCount: convertedMessages.length
        });
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      throw error;
    }
  }, [apiService]);

  // 加载历史消息
  const loadMoreHistory = useCallback(async (email: string, tempUserKey: string) => {
    console.log('尝试加载历史消息:', {
      isLoadingHistory,
      hasMoreHistory,
      currentPage,
      nextPage: currentPage + 1
    });
    
    if (isLoadingHistory || !hasMoreHistory) {
      console.log('跳过加载历史消息:', { isLoadingHistory, hasMoreHistory });
      return;
    }

    setIsLoadingHistory(true);
    try {
      await fetchMessages(email, tempUserKey, currentPage + 1, 10, false);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [isLoadingHistory, hasMoreHistory, currentPage, fetchMessages]);

  // 注意：已移除动画相关逻辑，不再需要清除 isNew 标记
  // 如果将来需要恢复动画，可以在这里添加清除逻辑

  return {
    messages,
    setMessages,
    addMessage,
    addMessages, // 新增批量添加方法
    updateMessageStatus,
    markMessageAsRead,
    fetchMessages,
    loadMoreHistory,
    isLoadingHistory,
    hasMoreHistory,
    currentPage,
    totalPages,
    lastMessageTimestamp
  };
}